import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { firebaseAuth } from "@/shared/lib/firebase";
import { useAuthStore } from "@/shared/store/authStore";

export const SESSION_EXPIRED_EVENT = "carevinco-session-expired";
export const SESSION_EXPIRED_MESSAGE_STORAGE_KEY = "carevinco-session-expired-message";

export type ApiErrorResponse = {
  success?: boolean;
  message?: string;
  error_code?: string;
  detail?: string | Array<{ msg?: string; message?: string }>;
  error?: {
    code?: string;
    message?: string;
    debug?: unknown;
  };
  debug?: unknown;
};

type QueuedRequest = {
  priority: number;
  resolve: (release: () => void) => void;
};

type QueuedApiRequest = QueuedRequest & {
  isReadOnly: boolean;
};

type CarevincoRequestConfig = InternalAxiosRequestConfig & {
  __carevincoDatabaseRetryCount?: number;
  __carevincoReleaseRequestSlot?: () => void;
};

const maxConcurrentApiRequests = 1;
const maxConcurrentAdminReadRequests = 2;
let activeApiRequests = 0;
let activeReadOnlyApiRequests = 0;
const apiRequestQueue: QueuedApiRequest[] = [];
const maxConcurrentBookingMutations = 1;
let activeBookingMutations = 0;
const bookingMutationQueue: QueuedRequest[] = [];

function isReadOnlyRequest(config: InternalAxiosRequestConfig) {
  const method = (config.method || "get").toLowerCase();
  return method === "get" || method === "head" || method === "options";
}

function isBookingMutation(config: InternalAxiosRequestConfig) {
  return !isReadOnlyRequest(config) && (config.url || "").includes("/bookings/");
}

function getRequestPriority(config: InternalAxiosRequestConfig) {
  const isMutation = !isReadOnlyRequest(config);
  const requestUrl = config.url || "";

  if (requestUrl.includes("/payments/") || (requestUrl.includes("/bookings/") && isMutation)) {
    return 5;
  }

  // Device registration is needed by the notification switch. Do not leave its
  // verification/deactivation reads behind continuously refreshed bookings.
  if (isMutation || requestUrl.includes("/notifications/device-token")) {
    return 4;
  }

  if (requestUrl.includes("/bookings")) {
    return 3;
  }

  if (
    requestUrl.includes("/providers/me") ||
    requestUrl.includes("/auth/me") ||
    requestUrl.includes("/patients")
  ) {
    return 2;
  }

  return 1;
}

function canStartApiRequest(request: QueuedApiRequest) {
  if (activeBookingMutations > 0) return false;

  const role = useAuthStore.getState().role;
  const isAdmin = role === "admin" || role === "super_admin";
  // Admin pages fetch several independent datasets. Allow a small read-only
  // batch while retaining the ordinary limit and an exclusive slot for writes.
  const limit = isAdmin && request.isReadOnly && activeReadOnlyApiRequests === activeApiRequests
    ? maxConcurrentAdminReadRequests
    : maxConcurrentApiRequests;

  return activeApiRequests < limit;
}

function startNextApiRequest() {
  while (apiRequestQueue.length && canStartApiRequest(apiRequestQueue[0])) {
    const next = apiRequestQueue.shift()!;
    activeApiRequests += 1;
    if (next.isReadOnly) activeReadOnlyApiRequests += 1;
    let released = false;
    next.resolve(() => {
      if (released) return;
      released = true;
      releaseNextApiRequest(next.isReadOnly);
    });
  }
}

function releaseNextApiRequest(isReadOnly: boolean) {
  activeApiRequests = Math.max(0, activeApiRequests - 1);
  if (isReadOnly) activeReadOnlyApiRequests = Math.max(0, activeReadOnlyApiRequests - 1);
  startNextApiRequest();
}

function releaseNextBookingMutation() {
  activeBookingMutations = Math.max(0, activeBookingMutations - 1);
  const next = bookingMutationQueue.shift();

  if (!next) {
    startNextApiRequest();
    return;
  }

  activeBookingMutations += 1;
  let released = false;
  next.resolve(() => {
    if (released) return;
    released = true;
    releaseNextBookingMutation();
  });
}

function acquireBookingMutationSlot(): Promise<() => void> {
  if (activeBookingMutations < maxConcurrentBookingMutations) {
    activeBookingMutations += 1;
    let released = false;
    return Promise.resolve(() => {
      if (released) return;
      released = true;
      releaseNextBookingMutation();
    });
  }

  return new Promise((resolve) => {
    bookingMutationQueue.push({
      priority: 5,
      resolve,
    });
  });
}

function acquireApiRequestSlot(config: InternalAxiosRequestConfig): Promise<() => void> {
  // Booking workflow actions must not sit behind slow dashboard/profile reads.
  // Keep them serialized with each other, but give them a dedicated lane so
  // Accept, OTP request, Start, and Complete are dispatched immediately.
  if (isBookingMutation(config)) {
    return acquireBookingMutationSlot();
  }

  return new Promise((resolve) => {
    const queuedRequest: QueuedApiRequest = {
      isReadOnly: isReadOnlyRequest(config),
      priority: getRequestPriority(config),
      resolve,
    };
    const insertionIndex = apiRequestQueue.findIndex((request) => request.priority < queuedRequest.priority);

    if (insertionIndex === -1) {
      apiRequestQueue.push(queuedRequest);
    } else {
      apiRequestQueue.splice(insertionIndex, 0, queuedRequest);
    }

    startNextApiRequest();
  });
}

function releaseApiRequest(config?: InternalAxiosRequestConfig) {
  const carevincoConfig = config as CarevincoRequestConfig | undefined;
  carevincoConfig?.__carevincoReleaseRequestSlot?.();
  if (carevincoConfig) {
    delete carevincoConfig.__carevincoReleaseRequestSlot;
  }
}

export const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8000/api/v1",
  timeout: 35_000,
});

const publicAuthPaths = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
];

let isHandlingSessionExpiry = false;

function handleSessionExpiry(message = "Your session has expired. Please sign in again.") {
  if (typeof window === "undefined" || isHandlingSessionExpiry) {
    return;
  }

  isHandlingSessionExpiry = true;

  try {
    window.sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_STORAGE_KEY, message);
  } catch {
    // Ignore storage failures and still redirect the user.
  }

  useAuthStore.getState().clearAuth();
  void firebaseAuth.signOut().catch(() => undefined);

  const isOnHomepage = window.location.pathname === "/" && !window.location.search && !window.location.hash;

  if (isOnHomepage) {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    window.setTimeout(() => {
      isHandlingSessionExpiry = false;
    }, 0);
    return;
  }

  window.location.replace("/");
}

apiClient.interceptors.request.use(
  async (config) => {
    const releaseRequestSlot = await acquireApiRequestSlot(config);
    (config as CarevincoRequestConfig).__carevincoReleaseRequestSlot = releaseRequestSlot;

    const requestUrl = config.url || "";
    const isPublicAuthRequest = publicAuthPaths.some((path) => requestUrl.includes(path));
    const accessToken = useAuthStore.getState().accessToken;

    if (isPublicAuthRequest) {
      delete config.headers.Authorization;
      return config;
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => {
    releaseApiRequest(response.config);
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    releaseApiRequest(error.config);

    const retryConfig = error.config as CarevincoRequestConfig | undefined;
    const method = (retryConfig?.method || "get").toLowerCase();
    const canSafelyRetry = method === "get" || method === "head";
    const retryCount = retryConfig?.__carevincoDatabaseRetryCount || 0;

    if (retryConfig && canSafelyRetry && retryCount < 1 && isDatabaseCapacityError(error)) {
      retryConfig.__carevincoDatabaseRetryCount = retryCount + 1;
      await new Promise((resolve) => setTimeout(resolve, 1_250));
      return apiClient.request(retryConfig);
    }

    const requestUrl = error.config?.url || "";
    const isPublicAuthRequest = publicAuthPaths.some((path) => requestUrl.includes(path));

    if (error.response?.status === 401 && !isPublicAuthRequest) {
      handleSessionExpiry(getApiErrorMessage(error));
    }

    return Promise.reject(error);
  },
);

export function isDatabaseCapacityError(error: unknown) {
  const err = error as AxiosError<ApiErrorResponse>;
  const code = err.response?.data?.error?.code || err.response?.data?.error_code;
  const message = getApiErrorMessage(error).toLowerCase();

  return (
    code === "DATABASE_ERROR" ||
    message.includes("queuepool limit") ||
    message.includes("connection timed out") ||
    message.includes("database") && message.includes("try again shortly")
  );
}

export function getApiErrorMessage(error: unknown): string {
  const err = error as AxiosError<ApiErrorResponse>;
  const data = err.response?.data;
  const firstValidationError = Array.isArray(data?.detail) ? data.detail[0] : undefined;
  const rawMessage =
    data?.error?.message ||
    data?.message ||
    (typeof data?.detail === "string" ? data.detail : undefined) ||
    firstValidationError?.message ||
    firstValidationError?.msg ||
    err.message;
  const message = rawMessage?.trim();

  if (message?.toLowerCase().includes("service with this name already exists")) {
    return "A service with this name already exists. Use a different name or edit the existing service.";
  }

  if (message?.toLowerCase().includes("value too long")) {
    return "One of the fields is too long. Shorten the text and try again.";
  }

  if (data?.error?.code === "DATABASE_ERROR" || message?.toLowerCase().includes("queuepool limit")) {
    return "Carevinco is handling another request. Please wait a moment while this page retries safely.";
  }

  if (err.response?.status === 401) {
    return "Your session has expired. Please sign in again.";
  }

  if (err.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (err.response?.status === 422) {
    return message || "Please check the form details and try again.";
  }

  return message || "We could not complete that action. Please try again.";
}
